import React, { useMemo } from 'react';
import sanitizer from '@helpers/sanitizer';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import { usePreferences } from '@context/preferences.context';
import useInitialRequestUrlQuery from '@hooks/use-initial-request-url-query';
import { ElasticSearchProduct } from '@interfaces/api/responses/elasticSearch';
import { getSeoBaseUrlFromLocale } from '@helpers/utils/seo-structured-data';
import { createCatalogProductSchema } from './product-schema-utils';

const MAX_NUMBER_OF_ITEMS = 10000;

const CatalogSchema: React.FC = () => {
  const products = useContextSelector(CatalogContext, (v) => v.products);
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const totalResults = useContextSelector(CatalogContext, (v) => v.totalResults);
  const { initialRequestUrl } = useInitialRequestUrlQuery();
  const { locale } = usePreferences();

  const schema = useMemo(() => {
    if (!products?.length || !queryResult?.items) {
      return null;
    }

    const origin = getSeoBaseUrlFromLocale(locale);

    const searchItemsById = new Map<string, ElasticSearchProduct>();
    queryResult.items.forEach((item: ElasticSearchProduct) => {
      searchItemsById.set(String(item.id), item);
    });

    const itemListElement = products.map((product, index: number) => {
      const searchItem = searchItemsById.get(product.id);

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: createCatalogProductSchema(product, searchItem, origin),
      };
    });

    const pageUrl =
      origin && initialRequestUrl ? `${origin}${initialRequestUrl.pathname}${initialRequestUrl.search}` : undefined;

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      ...(totalResults != null && { numberOfItems: Math.min(totalResults, MAX_NUMBER_OF_ITEMS) }),
      ...(pageUrl && { url: pageUrl }),
      itemListElement,
    };
  }, [products, queryResult, totalResults, initialRequestUrl, locale]);

  if (!schema) {
    return null;
  }

  return (
    <script
      data-cy="catalog-schema"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: sanitizer(JSON.stringify(schema)) }}
    />
  );
};

export default CatalogSchema;
